import { useMutation, useQueryClient } from "@tanstack/react-query";
import { QUERY_KEYS } from "@/constants/QUERY_KEYS";
import httpClient from "@/config/axios";
import { toast } from "@/components/toast/CustomToast";
import {
  buildSupportedWantedEntries,
  type AvoidEntriesByDate,
} from "../utils/wantedEntries";
import type {
  MonthlyMemoResponse,
  PreferencesLatestResponse,
} from "./useRosterWantedQueries";

interface SaveCalendarDataParams {
  targetData: Record<string, { type: string; comment: string | null }>;
  avoidShiftByDate?: AvoidEntriesByDate;
  activeDate: { year: number; month: number };
  isReset: boolean;
  groupId?: string;
  /** 이 화면이 기피근무까지 관리하는가. 기피 UI 가 있는 화면만 true.
   *  false/미지정이면 서버가 기존 기피근무를 **손대지 않는다** — 기피 UI 가 없는
   *  구 화면이 캘린더만 저장해도 기피가 통째로 지워지던 것을 막는다. */
  managesAvoid?: boolean;
  /** 함께 저장할 자연어 문장. 서버가 분석해 선호/기피로 갈라 병합한다. */
  request?: string;
}

type FinalSubmitParams = SaveCalendarDataParams;

interface RetractSubmissionParams {
  year: number;
  month: number;
  groupId?: string;
}

interface SaveMonthlyMemoParams {
  year: number;
  month: number;
  groupId?: string;
  memo: string;
}

function buildCalendarPreferencesPayload({
  targetData,
  avoidShiftByDate = {},
  activeDate,
  isReset,
  groupId,
  managesAvoid,
  request,
}: SaveCalendarDataParams) {
  const wantedEntries = isReset
    ? []
    : buildSupportedWantedEntries(targetData, avoidShiftByDate);
  const payload: Record<string, unknown> = {
    year: activeDate.year,
    month: activeDate.month,
    wanted_entries: wantedEntries,
  };
  if (managesAvoid) payload.manages_avoid = true;
  if (request !== undefined) payload.request = request;

  return groupId ? { ...payload, group_id: groupId } : payload;
}

function updatePreferencesLatestCache(
  queryClient: ReturnType<typeof useQueryClient>,
  activeDate: { year: number; month: number },
  groupId: string | undefined,
  data: PreferencesLatestResponse
) {
  queryClient.setQueryData<PreferencesLatestResponse>(
    QUERY_KEYS.preferences.latest(activeDate.year, activeDate.month, groupId),
    data
  );
}

type SaveWantedRequestParams = Omit<SaveCalendarDataParams, "request"> & {
  wantedInput: string;
};

function notifyPreferenceSaveNotices(data: PreferencesLatestResponse) {
  if (data.analysis?.message) {
    toast.info(data.analysis.message);
  }
  if (data.avoid_blocked?.message) {
    toast.warning(data.avoid_blocked.message);
  }
}

function getPreferenceMutationErrorMessage(error: unknown): string {
  return error instanceof Error
    ? error.message
    : "원티드 저장에 실패했습니다. 다시 시도해주세요.";
}

export const useSaveWantedRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ wantedInput, ...rest }: SaveWantedRequestParams) => {
      // ★ 문장만 보내면 안 된다. `wanted_entries` 가 없으면 서버가 레거시 경로로
      //   빠져 `{message}` 만 돌려주고(캐시 오염), 문장 해석이 0건일 때 화면에
      //   찍어둔 캘린더가 통째로 날아간다. 지금 화면 상태를 같이 실어 보내면
      //   서버가 병합하며 **같은 날짜는 캘린더 쪽이 이긴다.**
      const data = await httpClient.post<PreferencesLatestResponse>(
        "/preferences",
        buildCalendarPreferencesPayload({ ...rest, request: wantedInput })
      );
      return data;
    },
    onSuccess: (data, variables) => {
      const { year, month } = variables.activeDate;
      const { groupId } = variables;
      // 응답이 곧 최신 snapshot 이라 캐시에 바로 얹는다(재조회 왕복 제거).
      updatePreferencesLatestCache(queryClient, variables.activeDate, groupId, data);
      notifyPreferenceSaveNotices(data);
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.preferences.all(year, month, groupId),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.wanted.shiftRequests(year, month, groupId ?? ""),
      });
    },
    onError: (error) => {
      toast.error(getPreferenceMutationErrorMessage(error));
    },
  });
};

export const useSaveMonthlyMemo = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ year, month, groupId, memo }: SaveMonthlyMemoParams) => {
      const payload: {
        year: number;
        month: number;
        group_id?: string;
        monthly_memo: string;
      } = { year, month, monthly_memo: memo };
      if (groupId?.trim()) payload.group_id = groupId;

      return await httpClient.patch<MonthlyMemoResponse>(
        "/preferences/monthly-memo",
        payload
      );
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(
        QUERY_KEYS.preferences.monthlyMemo(
          variables.year,
          variables.month,
          variables.groupId
        ),
        data
      );
    },
  });
};

// 최종 제출 훅
export const useFinalSubmit = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variables: FinalSubmitParams) => {
      const payload = buildCalendarPreferencesPayload(variables);
      return await httpClient.post<PreferencesLatestResponse>(
        "/preferences/submit",
        payload
      );
    },
    onSuccess: (data, variables) => {
      const { activeDate, groupId } = variables;
      const { year, month } = activeDate;
      updatePreferencesLatestCache(queryClient, activeDate, groupId, data);
      notifyPreferenceSaveNotices(data);
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.preferences.schedule(year, month, groupId),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.preferences.all(year, month, groupId),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.preferences.latest(year, month, groupId),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.wanted.all(groupId),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.wanted.shiftRequests(year, month, groupId ?? ""),
      });
      // 알림 리스트 캐싱 무효화 (팝오버, 모달 모두)
      queryClient.invalidateQueries({ queryKey: ["alarms"] });
    },
  });
};

// 캘린더 데이터 저장 훅
export const useSaveCalendarData = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variables: SaveCalendarDataParams) => {
      const payload = buildCalendarPreferencesPayload(variables);
      return await httpClient.post<PreferencesLatestResponse>(
        "/preferences",
        payload
      );
    },
    onSuccess: (data, variables) => {
      const { year, month } = variables.activeDate;
      const { groupId } = variables;
      updatePreferencesLatestCache(
        queryClient,
        variables.activeDate,
        groupId,
        data
      );
      notifyPreferenceSaveNotices(data);
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.wanted.shiftRequests(year, month, groupId ?? ""),
      });
    },
    onError: (error) => {
      toast.error(getPreferenceMutationErrorMessage(error));
    },
  });
};

// 제출 철회 훅
export const useRetractSubmission = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ year, month }: RetractSubmissionParams) => {
      await httpClient.post(`/preferences/retract`, { year, month });
    },
    onSuccess: (_, variables) => {
      const { year, month, groupId } = variables;
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.preferences.all(year, month, groupId),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.preferences.latest(year, month, groupId),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.wanted.shiftRequests(year, month, groupId ?? ""),
      });
    },
  });
};
